import axios from "../../api/axios";

/**
 * Extract and format error messages from API response
 * @param {Object} error - Axios error object
 * @returns {Array} Array of formatted error messages
 */
const extractErrors = (error) => {
  if (!error) return ["En ukjent feil oppstod"];

  const errors = [];
  
  // Handle response errors
  if (error.response) {
    const { data, status } = error.response;
    
    // Handle validation errors (422 or 400)
    if (status === 422 || status === 400) {
      if (data?.errors) {
        // Rails validation errors format - can be array or object
        if (Array.isArray(data.errors)) {
          // Array format: ["Error message 1", "Error message 2"]
          errors.push(...data.errors);
        } else {
          // Object format: { field: ["error1", "error2"] }
          Object.keys(data.errors).forEach((field) => {
            const fieldErrors = Array.isArray(data.errors[field])
              ? data.errors[field]
              : [data.errors[field]];
            fieldErrors.forEach((err) => {
              errors.push(`${field}: ${err}`);
            });
          });
        }
      } else if (data?.error) {
        // Single error message
        errors.push(data.error);
      } else if (data?.message) {
        errors.push(data.message);
      } else if (typeof data === "string") {
        errors.push(data);
      }
    } else if (status === 500) {
      errors.push("Serverfeil: Vennligst prøv igjen senere");
    } else if (status === 401) {
      errors.push("Du er ikke autorisert til å utføre denne handlingen");
    } else if (status === 403) {
      errors.push("Du har ikke tilgang til å utføre denne handlingen");
    } else if (status === 404) {
      errors.push("Fraværet ble ikke funnet");
    } else if (data?.error || data?.message) {
      errors.push(data.error || data.message);
    } else {
      errors.push(`Feil ${status}: ${error.message || "Ukjent feil"}`);
    }
  } else if (error.request) {
    // Network error
    errors.push("Nettverksfeil: Sjekk internettforbindelsen din");
  } else {
    // Other errors
    errors.push(error.message || "En ukjent feil oppstod");
  }

  return errors.length > 0 ? errors : ["En ukjent feil oppstod"];
};

/**
 * Fetch absences with pagination and filters
 * @param {Object} controller - Query parameters (page, per_page, filters, etc.)
 * @returns {Promise} Promise resolving to response data or error
 */
export const fetchAbsences = async (controller = {}) => {
  try {
    const response = await axios({
      method: "get",
      url: "/absences/",
      params: controller,
    });
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      errors: extractErrors(error),
      status: error.response?.status || 500,
      data: null,
    };
  }
};

/**
 * Create a new absence
 * @param {Object} payload - Absence data
 * @param {Object} controller - Additional parameters (fields, etc.)
 * @returns {Promise} Promise resolving to response or error
 */
export async function createAbsence(payload, controller = {}) {
  try {
    const response = await axios.post(`/absences`, {
      absence: payload,
      params: controller,
    });
    
    // Check if response.data contains errors (shouldn't happen with 200, but be safe)
    if (response.data && response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
      return {
        success: false,
        errors: response.data.errors,
        status: response.status,
        data: null,
      };
    }
    
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      errors: extractErrors(error),
      status: error.response?.status || 500,
      data: null,
    };
  }
}

/**
 * Update an existing absence
 * @param {Object} payload - Absence data with id
 * @returns {Promise} Promise resolving to response or error
 */
export async function updateAbsence(payload) {
  try {
    const response = await axios.put(`/absences/${payload.id}`, {
      absence: payload,
    });
    
    // Check if response.data contains errors (shouldn't happen with 200, but be safe)
    if (response.data && response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
      return {
        success: false,
        errors: response.data.errors,
        status: response.status,
        data: null,
      };
    }
    
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      errors: extractErrors(error),
      status: error.response?.status || 500,
      data: null,
    };
  }
}

/**
 * Fetch a single absence by ID
 * @param {number|string} payload - Absence ID
 * @returns {Promise} Promise resolving to absence data or error
 */
export async function fetchAbsence(payload) {
  try {
    const response = await axios.get(`/absences/${payload}`);
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      errors: extractErrors(error),
      status: error.response?.status || 500,
      data: null,
    };
  }
}

/**
 * Delete an absence
 * @param {number|string} payload - Absence ID
 * @returns {Promise} Promise resolving to response or error
 */
export async function deleteAbsence(payload) {
  try {
    const response = await axios.delete(`/absences/${payload}`);
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      errors: extractErrors(error),
      status: error.response?.status || 500,
      data: null,
    };
  }
}

/**
 * Extract errors from blob response
 * @param {Blob} blob - Blob response
 * @returns {Promise<Array>} Array of error messages
 */
const extractBlobErrors = async (blob) => {
  try {
    const text = await blob.text();
    // Try to parse as JSON
    try {
      const json = JSON.parse(text);
      if (json.errors) {
        return Array.isArray(json.errors) ? json.errors : [json.errors];
      }
      if (json.error) {
        return [json.error];
      }
    } catch (e) {
      // Not JSON, might be HTML error page
      if (text.includes('Error') || text.includes('error')) {
        return ["Kunne ikke eksportere PDF"];
      }
    }
    return ["Kunne ikke eksportere PDF"];
  } catch (e) {
    return ["Kunne ikke eksportere PDF"];
  }
};

/**
 * Check if blob is a valid PDF
 * @param {Blob} blob - Blob to check
 * @returns {Promise<{isPdf: boolean, data: Blob|string}>}
 */
const checkBlobIsPdf = async (blob) => {
  try {
    // Read first few bytes to check PDF header without reading entire blob
    const slice = blob.slice(0, 4);
    const arrayBuffer = await slice.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const header = String.fromCharCode(...uint8Array);
    
    if (header.startsWith('%PDF')) {
      return { isPdf: true, data: blob };
    } else {
      // Read full blob to extract error
      const text = await blob.text();
      return { isPdf: false, data: text };
    }
  } catch (e) {
    return { isPdf: false, data: null };
  }
};

/**
 * Export absence summary as PDF
 * @param {Object} params - Parameters (user_id, from, to)
 * @returns {Promise} Promise resolving to PDF blob or error
 */
export async function exportAbsenceSummaryPDF(params = {}) {
  try {
    const response = await axios.get('/absences/summary/export_pdf', {
      params,
      responseType: 'blob',
    });
    
    // Check if response is actually a PDF
    if (response.data instanceof Blob) {
      const checkResult = await checkBlobIsPdf(response.data);
      if (checkResult.isPdf) {
        return {
          success: true,
          data: checkResult.data,
          status: response.status,
        };
      } else {
        // It's likely an error JSON
        const errors = await extractBlobErrors(response.data);
        return {
          success: false,
          errors,
          status: response.status,
          data: null,
        };
      }
    }
    
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    // Handle blob error responses
    if (error.response && error.response.data instanceof Blob) {
      const errors = await extractBlobErrors(error.response.data);
      return {
        success: false,
        errors,
        status: error.response.status,
        data: null,
      };
    }
    
    return {
      success: false,
      errors: extractErrors(error),
      status: error.response?.status || 500,
      data: null,
    };
  }
}

/**
 * Export single absence as PDF
 * @param {number|string} absenceId - Absence ID
 * @returns {Promise} Promise resolving to PDF blob or error
 */
export async function checkAbsenceForDate(userId, date) {
  try {
    const response = await axios.get(`/users/${userId}/absences`, {
      params: {
        date_from: date,
        date_too: date,
      },
    });
    if (response.status === 200) {
      const absences = Array.isArray(response.data) ? response.data : [];
      // Check if any absence overlaps with the date
      return absences.some((absence) => {
        const from = new Date(absence.date_from);
        const to = new Date(absence.date_too);
        const checkDate = new Date(date);
        return checkDate >= from && checkDate <= to;
      });
    }
    return false;
  } catch (error) {
    return false;
  }
}

export async function exportAbsencePDF(absenceId) {
  try {
    const response = await axios.get(`/absences/${absenceId}/pdf`, {
      responseType: 'blob',
    });
    
    // Check if response is actually a PDF
    if (response.data instanceof Blob) {
      const checkResult = await checkBlobIsPdf(response.data);
      if (checkResult.isPdf) {
        return {
          success: true,
          data: checkResult.data,
          status: response.status,
        };
      } else {
        // It's likely an error JSON
        const errors = await extractBlobErrors(response.data);
        return {
          success: false,
          errors,
          status: response.status,
          data: null,
        };
      }
    }
    
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    // Handle blob error responses
    if (error.response && error.response.data instanceof Blob) {
      const errors = await extractBlobErrors(error.response.data);
      return {
        success: false,
        errors,
        status: error.response.status,
        data: null,
      };
    }
    
    return {
      success: false,
      errors: extractErrors(error),
      status: error.response?.status || 500,
      data: null,
    };
  }
}
